import { useCallback, useEffect, useState } from 'react';
import { useAsync } from '@nxsys/hooks/useAsync';
import { getAnalyticsRequiredAlertsList } from '@nxsys/services/api/analytics';
import {
  GetAnalyticsRequiredAlertsListParams,
  GetAnalyticsRequiredAlertsListResponse,
  RequiredAlertsListType,
} from '@nxsys/types/api/analytics';
import { showMessage } from '@nxsys/utils/message';
import { getErrorMessage } from '@nxsys/utils/getErrorMessage';

type UseGetRequiredAlertsListParams = {
  type: RequiredAlertsListType;
};

type UseGetRequiredAlertsListReturn = {
  loading: boolean;
  error: Error | null;
  data: GetAnalyticsRequiredAlertsListResponse | null;
  fetchRequiredAlertsList: (
    params: Omit<GetAnalyticsRequiredAlertsListParams, 'type'>,
  ) => Promise<void>;
  queryParams: GetAnalyticsRequiredAlertsListParams;
  searchTerm: string;
  setSearchTerm: (value: string) => void;
};

export const useGetRequiredAlertsList = ({
  type,
}: UseGetRequiredAlertsListParams): UseGetRequiredAlertsListReturn => {
  const {
    loading,
    error,
    value: data,
    run,
  } = useAsync<
    GetAnalyticsRequiredAlertsListResponse,
    GetAnalyticsRequiredAlertsListParams
  >(getAnalyticsRequiredAlertsList);

  const [searchTerm, setSearchTerm] = useState('');
  const [queryParams, setQueryParams] =
    useState<GetAnalyticsRequiredAlertsListParams>({
      type,
      page: 0,
      size: 20,
    });

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  const fetchRequiredAlertsList = useCallback(
    async (params: Omit<GetAnalyticsRequiredAlertsListParams, 'type'>) => {
      const fullParams = { ...params, type };
      setQueryParams(fullParams);
      await run(fullParams);
    },
    [type, run],
  );

  return {
    loading,
    error,
    data,
    fetchRequiredAlertsList,
    queryParams,
    searchTerm,
    setSearchTerm,
  };
};
